import { useState } from 'react';

import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { EventDialog } from './event-dialog';
import { ExportInviteDialog } from './export-invite-dialog';
import { CalculatedDateNotice, useCalculatedDate } from './procedural-calculated-date';
import { flashEventRow, formatDate, formatDateRange, mapDeltaTypeToCounting } from './procedural-helpers';
import {
  CONFIDENCE_LABELS,
  COUNTING_LABEL,
  DateTbdLabel,
  DetailCell,
  DetailsAccordion,
  SourceFileButton,
  UnconfirmedChip,
  formatRelationship,
} from './procedural-row-parts';
import { ProceduralSourceRef } from './procedural-source-drawer';
import { Permission } from '../../../../../../../../backend/src/auth/permissions/permission';
import { EventConfidence } from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-enums';
import type { ProceduralTimelineEvent } from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event.entity';
import { useDeleteProceduralEvent } from '@/api/mutations/useDeleteProceduralEvent';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/new/components/shadcn/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { formatTimeZoneLabel } from '@/new/lib/time-zones';
import { useTimelineCalculatorStore } from '@/new/stores/timeline-calculator-store';

interface ProceduralEventRowProps {
  event: ProceduralTimelineEvent;
  eventsById: Record<string, ProceduralTimelineEvent>;
  matterName: string;
  onOpenSource: (source: ProceduralSourceRef) => void;
  /** Null = editable. Non-null disables the edit button and is used as the tooltip text. */
  editDisabledReason: string | null;
}

function scrollToEvent(eventId: string) {
  const el = document.getElementById(`procedural-event-${eventId}`);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  flashEventRow(eventId);
}

export function ProceduralEventRow({
  event,
  eventsById,
  matterName,
  onOpenSource,
  editDisabledReason,
}: ProceduralEventRowProps) {
  const isEditDisabled = editDisabledReason !== null;
  const { matterId } = useParams();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { mutate: deleteEvent, isLoading: isDeleting } = useDeleteProceduralEvent();
  const openCalculator = useTimelineCalculatorStore((s) => s.open);

  const latestChunk = event.sourceChunks?.[event.sourceChunks.length - 1];
  const sourceFileName = latestChunk?.file?.name ?? null;
  const dependents = Object.values(eventsById).filter((e) => e.anchorEventId === event.id);
  const anchorName = event.anchorEventId ? (eventsById[event.anchorEventId]?.name ?? null) : null;
  const relationship = formatRelationship(event.deltaInterval, event.deltaIntervalType, anchorName);
  const dateRangeText = formatDateRange(event.startDateTime, event.endDateTime);

  const handleSourceClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!latestChunk?.file) return;
    onOpenSource({
      fileId: latestChunk.file.id,
      fileName: latestChunk.file.name,
      pageNumber: latestChunk.pageNumber,
      chunkId: latestChunk.id,
      content: latestChunk.content,
      boundingBox: latestChunk.boundingBox,
    });
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditOpen(true);
  };

  const anchorEvent = event.anchorEventId ? (eventsById[event.anchorEventId] ?? null) : null;
  const calculatedDate = useCalculatedDate({
    matterId,
    anchorStartDateTime: anchorEvent?.startDateTime ?? null,
    days: event.deltaInterval?.days ?? null,
    deltaIntervalType: event.deltaIntervalType,
    jurisdiction: event.jurisdiction,
    currentStartDateTime: event.startDateTime,
  });

  const openCalc = () => {
    if (!matterId) return;
    const anchorStart = anchorEvent?.startDateTime ?? event.startDateTime;
    openCalculator({
      matterId,
      eventId: event.id,
      eventName: event.name,
      startDate: anchorStart ? new Date(anchorStart) : null,
      days: event.deltaInterval?.days ?? 0,
      counting: mapDeltaTypeToCounting(event.deltaIntervalType, event.jurisdiction),
      timeZone: event.timeZone ?? null,
      endDate: event.endDateTime ? new Date(event.endDateTime) : null,
      hasDependents: dependents.length > 0,
    });
  };

  const handleCalculatorClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    openCalc();
  };

  const handleConfirmDelete = () => {
    if (!matterId) return;
    deleteEvent(
      { matterId, eventId: event.id },
      {
        onSuccess: () => {
          toast.success('Event deleted.');
          setIsDeleteOpen(false);
        },
        onError: (error) => {
          toast.error(error.message || 'Failed to delete event.');
        },
      },
    );
  };

  return (
    <div
      id={`procedural-event-${event.id}`}
      className="mt-0.5 w-full rounded-lg border border-gray-200 bg-white px-4 py-3 transition-shadow duration-500"
    >
      <div className="flex w-full flex-col gap-2 pr-2 text-left">
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-2">
            <p className="text-sm text-gray-500">{dateRangeText ?? <DateTbdLabel />}</p>
            <CalculatedDateNotice state={calculatedDate} />
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {!event.isConfirmed && <UnconfirmedChip />}
            {sourceFileName && <SourceFileButton fileName={sourceFileName} onClick={handleSourceClick} />}
            {event.anchorEventId !== null && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="iconSm"
                    onClick={handleCalculatorClick}
                    aria-label="Open timeline calculator"
                  >
                    <Icon className="lucide--calculator" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Open timeline calculator</TooltipContent>
              </Tooltip>
            )}
            {event.startDateTime !== null ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="iconSm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsExportOpen(true);
                    }}
                    aria-label="Send as calendar invite"
                  >
                    <Icon className="lucide--calendar-plus" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Send as calendar invite</TooltipContent>
              </Tooltip>
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0} className="inline-flex cursor-not-allowed">
                    <Button type="button" variant="ghost" size="iconSm" disabled aria-label="Send as calendar invite">
                      <Icon className="lucide--calendar-plus" />
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Add a date to this event to share it as a calendar invite.</TooltipContent>
              </Tooltip>
            )}
            <PermissionGate permission={Permission.ProceduralTimelineEdit} uiMode="disabled">
              {isEditDisabled ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className="inline-flex cursor-not-allowed">
                      <Button type="button" variant="ghost" size="iconSm" disabled aria-label="Edit event">
                        <Icon className="lucide--pencil" />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{editDisabledReason}</TooltipContent>
                </Tooltip>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="iconSm"
                      onClick={handleEditClick}
                      aria-label="Edit event"
                    >
                      <Icon className="lucide--pencil" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Edit event</TooltipContent>
                </Tooltip>
              )}
            </PermissionGate>
            <PermissionGate permission={Permission.ProceduralTimelineEdit} uiMode="disabled">
              <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    className="-mr-1 p-0 text-gray-400 hover:text-gray-700"
                    aria-label="Delete event"
                  >
                    <Icon className="lucide--x" />
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Delete event</DialogTitle>
                    <DialogDescription>This action cannot be undone.</DialogDescription>
                  </DialogHeader>
                  <p className="text-sm">
                    Delete <span className="font-medium">{event.name}</span>?
                  </p>
                  {dependents.length > 0 && (
                    <p className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 px-3 text-xs text-amber-800">
                      <Icon className="lucide--triangle-alert mt-0.5 shrink-0" />
                      <span>
                        {dependents.length === 1
                          ? `“${dependents[0].name}” is calculated based on this event. Deleting will break that relationship.`
                          : `${dependents.length} events are calculated based on this event. Deleting will break those relationships.`}
                      </span>
                    </p>
                  )}
                  <DialogFooter>
                    <Button variant="ghost" onClick={() => setIsDeleteOpen(false)} disabled={isDeleting}>
                      Cancel
                    </Button>
                    <Button variant="destructive" onClick={handleConfirmDelete} disabled={isDeleting}>
                      <Icon className={isDeleting ? 'lucide--loader-2 animate-spin' : 'lucide--trash-2'} />
                      Delete event
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </PermissionGate>
          </div>
        </div>

        <p className="-mt-1.5 min-w-0 truncate text-lg text-gray-900">
          <Tooltip>
            <TooltipTrigger asChild>
              <span>{event.name}</span>
            </TooltipTrigger>
            <TooltipContent>{event.name}</TooltipContent>
          </Tooltip>
        </p>

        {relationship && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => scrollToEvent(event.anchorEventId!)}
            aria-label="Scroll to linked event"
            className="-mt-1 gap-1.5 self-start p-0 text-left text-xs font-light text-gray-500 hover:bg-transparent hover:text-gray-700"
          >
            <Icon className="lucide--link shrink-0 text-sky-600" />
            {relationship}
          </Button>
        )}

        {event.description && <p className="text-sm text-gray-600">{event.description}</p>}

        <DetailsAccordion className="mt-1">
          <DetailCell label="Created by">{event.createdBy?.email ?? 'Unknown'}</DetailCell>
          <DetailCell label="Created date">{formatDate(event.createdAt)}</DetailCell>
          <DetailCell label="Last updated">{formatDate(event.updatedAt)}</DetailCell>
          {event.confidence !== EventConfidence.Manual && (
            <DetailCell label="Confidence">{CONFIDENCE_LABELS[event.confidence]}</DetailCell>
          )}
          <DetailCell label="Time zone">{event.timeZone ? formatTimeZoneLabel(event.timeZone) : 'Not set'}</DetailCell>
          {event.anchorEventId !== null && (
            <DetailCell label="Interval basis">{COUNTING_LABEL[event.deltaIntervalType]}</DetailCell>
          )}
        </DetailsAccordion>
      </div>

      {matterId && (
        <EventDialog
          event={event}
          eventsById={eventsById}
          matterId={matterId}
          open={isEditOpen}
          onOpenChange={setIsEditOpen}
        />
      )}
      <ExportInviteDialog event={event} matterName={matterName} open={isExportOpen} onOpenChange={setIsExportOpen} />
    </div>
  );
}
